<script setup lang="ts">
// Line icons on a 16px grid (1.5 stroke, round joins), like the app's other inline icons.
// Transport icons (play, pause, previous, next) are filled, as players draw them.
withDefaults(
  defineProps<{
    name: 'gear' | 'people' | 'phone' | 'plus' | 'play' | 'pause' | 'previous' | 'next' | 'more' | 'down' | 'shuffle' | 'close' | 'queue' | 'left' | 'right' | 'check' | 'upload' | 'stream' | 'eraser' | 'search' | 'mic' | 'mic-off' | 'video' | 'video-off' | 'screen' | 'expand' | 'collapse' | 'chat' | 'hangup' | 'speaker'
    /** 16px, 20px or 24px. */
    size?: 4 | 5 | 6
  }>(),
  { size: 4 },
)
</script>

<template>
  <svg
    viewBox="0 0 16 16"
    :class="size === 6 ? 'h-6 w-6' : size === 5 ? 'h-5 w-5' : 'h-4 w-4'"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <template v-if="name === 'gear'">
      <!-- Six square teeth (tips at r=7, roots at r=5) round a hub -->
      <path d="M12.71 6.33L14.83 6.47L14.83 9.53L12.71 9.67L11.80 11.25L12.74 13.15L10.09 14.68L8.91 12.92L7.09 12.92L5.91 14.68L3.26 13.15L4.20 11.25L3.29 9.67L1.17 9.53L1.17 6.47L3.29 6.33L4.20 4.75L3.26 2.85L5.91 1.32L7.09 3.08L8.91 3.08L10.09 1.32L12.74 2.85L11.80 4.75Z" />
      <circle cx="8" cy="8" r="2" />
    </template>
    <template v-else-if="name === 'search'">
      <circle cx="6.75" cy="6.75" r="4.75" />
      <path d="M10.25 10.25L14.5 14.5" />
    </template>
    <template v-else-if="name === 'people'">
      <circle cx="6" cy="5.5" r="2.25" />
      <path d="M1.75 13.5a4.25 4.25 0 0 1 8.5 0" />
      <path d="M10.5 3.5a2.25 2.25 0 0 1 0 4.25M12 9.75a4.25 4.25 0 0 1 2.25 3.75" />
    </template>
    <!-- A handset: calls start as voice; video is turned on in the call -->
    <path v-else-if="name === 'phone'" d="M3.5 2h2.25l1.25 3-1.5 1.1a7.5 7.5 0 0 0 3.4 3.4L10 8l3 1.25v2.25a1.5 1.5 0 0 1-1.5 1.5A10.5 10.5 0 0 1 2 3.5 1.5 1.5 0 0 1 3.5 2z" />
    <!-- The handset put down: leaving a call -->
    <path v-else-if="name === 'hangup'" d="M3.5 2h2.25l1.25 3-1.5 1.1a7.5 7.5 0 0 0 3.4 3.4L10 8l3 1.25v2.25a1.5 1.5 0 0 1-1.5 1.5A10.5 10.5 0 0 1 2 3.5 1.5 1.5 0 0 1 3.5 2z" transform="rotate(135 8 8)" />
    <!-- A capsule microphone on its stand; struck through when muted -->
    <template v-else-if="name === 'mic' || name === 'mic-off'">
      <rect x="6" y="1.75" width="4" height="7.5" rx="2" />
      <path d="M3.5 7.5a4.5 4.5 0 0 0 9 0M8 12v2.25" />
      <path v-if="name === 'mic-off'" d="M2.5 2.5l11 11" />
    </template>
    <!-- A camera body and lens; struck through when off -->
    <template v-else-if="name === 'video' || name === 'video-off'">
      <rect x="1.5" y="4" width="9" height="8" rx="1" />
      <path d="M10.5 7l4-2.5v7l-4-2.5" />
      <path v-if="name === 'video-off'" d="M2.5 2.5l11 11" />
    </template>
    <!-- A monitor with an arrow up out of it: sharing the screen -->
    <template v-else-if="name === 'screen'">
      <rect x="1.5" y="2.5" width="13" height="9" rx="1" />
      <path d="M5.5 14h5M8 11.5V14M8 9V5M6 6.75l2-2 2 2" />
    </template>
    <path v-else-if="name === 'expand'" d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5L9 7M2.5 13.5L7 9" />
    <path v-else-if="name === 'collapse'" d="M13.5 2.5L9.5 6.5M9.5 3v3.5H13M2.5 13.5l4-4M3 9.5h3.5V13" />
    <path v-else-if="name === 'chat'" d="M2 3h12v8H7l-3 2.5V11H2z" />
    <!-- A loudspeaker with sound coming out -->
    <template v-else-if="name === 'speaker'">
      <path d="M2 6h2.5L8 3v10L4.5 10H2z" />
      <path d="M10.5 6a2.75 2.75 0 0 1 0 4M12.5 4a5.5 5.5 0 0 1 0 8" />
    </template>
    <path v-else-if="name === 'play'" d="M4.5 2.5v11l9-5.5z" fill="currentColor" />
    <template v-else-if="name === 'pause'">
      <rect x="3.5" y="2.5" width="3" height="11" fill="currentColor" stroke="none" />
      <rect x="9.5" y="2.5" width="3" height="11" fill="currentColor" stroke="none" />
    </template>
    <template v-else-if="name === 'previous'">
      <rect x="2.5" y="2.5" width="1.75" height="11" fill="currentColor" stroke="none" />
      <path d="M13.5 2.5v11L5.5 8z" fill="currentColor" />
    </template>
    <template v-else-if="name === 'next'">
      <path d="M2.5 2.5v11l8-5.5z" fill="currentColor" />
      <rect x="11.75" y="2.5" width="1.75" height="11" fill="currentColor" stroke="none" />
    </template>
    <template v-else-if="name === 'more'">
      <circle cx="3" cy="8" r="1.25" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="1.25" fill="currentColor" stroke="none" />
      <circle cx="13" cy="8" r="1.25" fill="currentColor" stroke="none" />
    </template>
    <path v-else-if="name === 'down'" d="M3.5 6l4.5 4.5L12.5 6" />
    <path v-else-if="name === 'left'" d="M10 3.5L5.5 8l4.5 4.5" />
    <path v-else-if="name === 'right'" d="M6 3.5L10.5 8 6 12.5" />
    <path v-else-if="name === 'check'" d="M3 8.5l3.25 3.25L13 5" />
    <!-- An arrow up out of a tray -->
    <path v-else-if="name === 'upload'" d="M8 10.5V2.5M4.75 5.5L8 2.25l3.25 3.25M2.5 9.5v4h11v-4" />
    <!-- A phone with signal beside it: what this device streams -->
    <template v-else-if="name === 'stream'">
      <rect x="2" y="3" width="6.5" height="10.5" rx="1" />
      <path d="M4.5 11.5h1.5" />
      <path d="M10.75 6.25a2.5 2.5 0 0 1 0 3.5M12.5 4.5a5 5 0 0 1 0 7" />
    </template>
    <path v-else-if="name === 'shuffle'" d="M1.5 4.5h3l6 7h4M1.5 11.5h3l1.5-1.75M10 6.25l.5-.75h4M12.5 3.5l2 2-2 2M12.5 9.5l2 2-2 2" />
    <path v-else-if="name === 'close'" d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    <!-- A tilted block eraser with its rubber end, on the line it rubs out -->
    <template v-else-if="name === 'eraser'">
      <path d="M9.5 2.25l4.25 4.25-6.5 6.5H4.75L2.25 10.5z" />
      <path d="M6 5.75l4.25 4.25M7.25 13h6.5" />
    </template>
    <path v-else-if="name === 'queue'" d="M1.5 3.5h9M1.5 7.5h9M1.5 11.5h5M11.5 9.5v5M9 12h5" />
    <path v-else d="M8 3v10M3 8h10" />
  </svg>
</template>
